import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/lib/store/authStore';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { validateLineState, clearLineState, getLineIntent } from '@/lib/api/controlPlaneClient';
import { RegistrationWizard } from './components/registration/RegistrationWizard';

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authHasHydrated = useAuthStore((state) => state.hasHydrated);

  const { setLineMode, setIntent, hasHydrated: regHasHydrated, isLineMode } = useRegistrationStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (authHasHydrated && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authHasHydrated, isAuthenticated, navigate]);

  // Handle LINE params from callback redirect
  useEffect(() => {
    if (!regHasHydrated) return;

    // Check for LINE params from callback redirect
    const newUser = searchParams.get('new_user');
    const lineUserId = searchParams.get('line_user_id');
    const firstName = searchParams.get('first_name');
    const state = searchParams.get('state');

    // If we have LINE params and not already in LINE mode
    if (newUser === '1' && lineUserId && state && !isLineMode) {
      // Validate CSRF state
      if (!validateLineState(state)) {
        // Invalid state - security issue, redirect to login
        navigate('/login', { replace: true });
        return;
      }

      // Clear LINE state (single use)
      clearLineState();

      // Initialize LINE mode in registration store
      setLineMode(
        lineUserId,
        firstName || '',
        searchParams.get('last_name') || '',
        searchParams.get('picture_url') || ''
      );

      // Recover intent from sessionStorage (stored before LINE OAuth redirect)
      const storedIntent = getLineIntent();
      if (storedIntent) {
        setIntent(storedIntent);
      }

      // Clear URL params to prevent re-processing on refresh
      window.history.replaceState({}, '', '/register');
    }
  }, [regHasHydrated, searchParams, setLineMode, setIntent, navigate, isLineMode]);

  // Handle ?intent= for email registration flow (e.g. /register?intent=chat)
  useEffect(() => {
    if (!regHasHydrated) return;
    const intentParam = searchParams.get('intent');
    if (intentParam) {
      setIntent(intentParam);
    }
  }, [regHasHydrated, searchParams, setIntent]);

  return <RegistrationWizard />;
}
